// TODO(stripe-reimpl): this file has legacy Stripe billing code, disabled while we're betaAccess-only. Rebuild on the new billing system.
import React, { useState } from "react";
import { Alert, Button } from "@mui/material";
import { SettingsRow, SettingsSection } from "../SettingsSection";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import capitalize from "../../../utils/helpers/capitalize";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { useAuth } from "hooks/useAuth";
import { format, fromUnixTime } from "date-fns";

const PlanManagement = ({ subscriptionTier, loading }) => {
  const [actionLoading, setActionLoading] = useState(null);
  const { userData, subscriptionData } = useAuth();
  const navigate = useNavigate();

  const handlePortalAction = async (flowType) => {
    setActionLoading(flowType);
    try {
      const createPortalSession = firebase
        .functions()
        .httpsCallable("createPortalSession");

      const { data } = await createPortalSession({
        successUrl: window.location.href,
        cancelUrl: window.location.href,
        role: userData?.role,
        stripeId: userData?.stripeId,
        flowType,
        subscriptionId: subscriptionData?.id,
      });

      if (data.url) {
        window.location.assign(data.url);
      }
    } catch (error) {
      console.error("Error opening portal:", error);
      alert("Failed to open subscription management. Please try again.");
    }
  };

  const handleManageBilling = async () => {
    setActionLoading("manage_billing");
    try {
      const createPortalSession = firebase
        .functions()
        .httpsCallable("createPortalSession");

      const { data } = await createPortalSession({
        successUrl: window.location.href,
        cancelUrl: window.location.href,
        role: userData?.role,
        stripeId: userData?.stripeId,
        // No flowType = regular portal with billing history, payment methods, invoices
      });

      if (data.url) {
        window.location.assign(data.url);
      }
    } catch (error) {
      console.error("Error opening portal:", error);
      alert("Failed to open billing management. Please try again.");
    }
  };

  const handleChangePlan = () => {
    // Navigate to your upgrade page instead of portal
    navigate("/dashboard/upgrade");
  };

  const formatDate = (timestamp) => {
    // Handle Firebase timestamp objects
    return format(timestamp.toDate(), "MMMM d, yyyy");
  };

  // Get appropriate plan name and button text based on user role
  const getPlanName = () => {
    if (userData?.role === "facility") {
      // Only show "CareMap Plus" if they actually have subscription data
      return subscriptionData ? "CareMap Plus" : "Unknown Plan";
    }
    return `${capitalize(subscriptionTier || "medium")} Plan`;
  };

  const getChangePlanButtonText = () => {
    if (userData?.role === "facility") {
      return "Change billing cycle";
    }
    return "Change";
  };

  const cancelling = subscriptionData?.cancel_at_period_end;

  return (
    <SettingsSection title="Plan">
      {cancelling && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Your subscription is scheduled to cancel on{" "}
          {formatDate(subscriptionData.current_period_end)}. Please renew it
          below to keep your access.
        </Alert>
      )}
      <SettingsRow
        label="Current plan"
        detail={getPlanName()}
        action={
          !cancelling && (
            <Button variant="outlined" size="small" onClick={handleChangePlan}>
              {getChangePlanButtonText()}
            </Button>
          )
        }
      />
      <SettingsRow
        label="Billing"
        detail="Invoices and payment methods"
        action={
          <LoadingButton
            variant="outlined"
            size="small"
            loading={actionLoading === "manage_billing"}
            disabled={!!actionLoading}
            onClick={handleManageBilling}
          >
            {cancelling ? "Renew" : "Manage"}
          </LoadingButton>
        }
      />
      {!cancelling && (
        <SettingsRow
          label="Subscription"
          detail="Cancel at the end of the billing period"
          action={
            <LoadingButton
              variant="outlined"
              size="small"
              color="error"
              loading={actionLoading === "subscription_cancel"}
              disabled={!!actionLoading}
              onClick={() => handlePortalAction("subscription_cancel")}
            >
              Cancel
            </LoadingButton>
          }
        />
      )}
    </SettingsSection>
  );
};

export default PlanManagement;
